import { z } from "zod";

const TipTapMarkSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("bold") }),
    z.object({ type: z.literal("italic") }),
    z.object({ type: z.literal("underline") }),
    z.object({ type: z.literal("strike") }),
    z.object({ type: z.literal("code") }),
    z.object({
        type: z.literal("link"),
        attrs: z.object({
            // Storage-loose: accept anything non-empty. The renderer-side
            // helper `parseLinkHref` (packages/shared/tiptap/link.ts) classifies
            // each href into a discriminated union (internal path / external
            // URL / anchor) so consumers don't have to reinvent the heuristic.
            href: z.string().min(1),
            target: z.string().nullable().optional(),
            rel: z.string().nullable().optional(),
            title: z.string().nullable().optional(),
        }),
    }),
]);

export type TipTapMark = z.infer<typeof TipTapMarkSchema>;

export type TipTapNode =
    | { type: "text"; text: string; marks?: TipTapMark[] }
    | { type: "hardBreak" }
    | { type: "paragraph"; content?: TipTapNode[] }
    | {
          type: "heading";
          attrs: { level: 1 | 2 | 3 | 4 | 5 | 6 };
          content?: TipTapNode[];
      }
    | { type: "blockquote"; content: TipTapNode[] }
    | { type: "bulletList"; content: TipTapNode[] }
    | { type: "orderedList"; attrs?: { start?: number }; content: TipTapNode[] }
    | { type: "listItem"; content: TipTapNode[] }
    | { type: "horizontalRule" }
    | { type: "table"; content: TipTapNode[] }
    | { type: "tableRow"; content: TipTapNode[] }
    | {
          type: "tableCell";
          attrs?: {
              colspan?: number;
              rowspan?: number;
              colwidth?: number[] | null;
          };
          content: TipTapNode[];
      }
    | {
          type: "tableHeader";
          attrs?: {
              colspan?: number;
              rowspan?: number;
              colwidth?: number[] | null;
          };
          content: TipTapNode[];
      }
    | {
          type: "image";
          attrs: {
              src: string;
              alt?: string | null;
              title?: string | null;
          };
      }
    | {
          type: "mediaEmbed";
          attrs: {
              kind: "youtube" | "vimeo" | "video" | "applePodcast";
              url: string;
              variant?: "episode" | "show" | null;
          };
      }
    | {
          type: "customBlock";
          attrs: {
              kind: string;
              version: number;
              data?: Record<string, unknown>;
          };
          content?: TipTapNode[];
      };

const TableCellAttrsSchema = z
    .object({
        colspan: z.number().int().optional(),
        rowspan: z.number().int().optional(),
        colwidth: z.array(z.number()).nullable().optional(),
    })
    .optional();

const TipTapNodeSchema: z.ZodType<TipTapNode> = z.lazy(() =>
    z.discriminatedUnion("type", [
        z.object({
            type: z.literal("text"),
            text: z.string().min(1),
            marks: z.array(TipTapMarkSchema).optional(),
        }),
        z.object({ type: z.literal("hardBreak") }),
        z.object({
            type: z.literal("paragraph"),
            content: z.array(TipTapNodeSchema).optional(),
        }),
        z.object({
            type: z.literal("heading"),
            attrs: z.object({
                level: z.union([
                    z.literal(1),
                    z.literal(2),
                    z.literal(3),
                    z.literal(4),
                    z.literal(5),
                    z.literal(6),
                ]),
            }),
            content: z.array(TipTapNodeSchema).optional(),
        }),
        z.object({
            type: z.literal("blockquote"),
            content: z.array(TipTapNodeSchema),
        }),
        // Lists nest naturally — bulletList/orderedList may contain listItems,
        // which may contain bulletList/orderedList again. Covers ul, ol, ul→ol,
        // ol→ul without any indent attribute.
        z.object({
            type: z.literal("bulletList"),
            content: z.array(TipTapNodeSchema),
        }),
        z.object({
            type: z.literal("orderedList"),
            attrs: z.object({ start: z.number().int().optional() }).optional(),
            content: z.array(TipTapNodeSchema),
        }),
        z.object({
            type: z.literal("listItem"),
            content: z.array(TipTapNodeSchema),
        }),
        z.object({ type: z.literal("horizontalRule") }),
        z.object({
            type: z.literal("table"),
            content: z.array(TipTapNodeSchema),
        }),
        z.object({
            type: z.literal("tableRow"),
            content: z.array(TipTapNodeSchema),
        }),
        z.object({
            type: z.literal("tableCell"),
            attrs: TableCellAttrsSchema,
            content: z.array(TipTapNodeSchema),
        }),
        z.object({
            type: z.literal("tableHeader"),
            attrs: TableCellAttrsSchema,
            content: z.array(TipTapNodeSchema),
        }),
        z.object({
            type: z.literal("image"),
            attrs: z.object({
                // Image src parallels link href: relative `/uploads/...` paths
                // are valid alongside fully-qualified URLs. Renderers resolve
                // against the current origin; keeping validation loose avoids
                // round-trip rejections for legacy assets.
                src: z.string().min(1),
                alt: z.string().nullable().optional(),
                title: z.string().nullable().optional(),
            }),
        }),
        z.object({
            type: z.literal("mediaEmbed"),
            attrs: z.object({
                kind: z.enum(["youtube", "vimeo", "video", "applePodcast"]),
                url: z.string().url(),
                variant: z.enum(["episode", "show"]).nullable().optional(),
            }),
        }),
        // Prototyping escape hatch — production blocks should be first-class
        // discriminated-union variants so unknown blocks fail validation.
        z.object({
            type: z.literal("customBlock"),
            attrs: z.object({
                kind: z.string().min(1),
                version: z.number().int().min(1).default(1),
                data: z.record(z.string(), z.unknown()).optional(),
            }),
            content: z.array(TipTapNodeSchema).optional(),
        }),
    ]),
);

export const TipTapDocSchema = z.object({
    type: z.literal("doc"),
    content: z.array(TipTapNodeSchema).min(1),
});

export type TipTapDoc = z.infer<typeof TipTapDocSchema>;

export function isTipTapDoc(value: unknown): value is TipTapDoc {
    return TipTapDocSchema.safeParse(value).success;
}

export function emptyTipTapDoc(): TipTapDoc {
    return {
        type: "doc",
        content: [{ type: "paragraph" }],
    };
}

/**
 * Wrap a plain string into a single-paragraph TipTap doc. Used by surfaces
 * that haven't (yet) adopted the rich-text editor — DM textarea, navigator
 * social comment textarea — so they can still satisfy the contentJson-only
 * API contract.
 */
export function plaintextToTipTapDoc(text: string): TipTapDoc {
    const trimmed = text.trim();
    if (!trimmed) return emptyTipTapDoc();
    return {
        type: "doc",
        content: [
            {
                type: "paragraph",
                content: [{ type: "text", text }],
            },
        ],
    };
}
